import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import axios from "axios";
import InstallPWA from "@/components/InstallPWA";
import UpdateNotification from "@/components/UpdateNotification";
import DynamicManifest from "@/components/DynamicManifest";
import GlobalAutoSync from "@/components/GlobalAutoSync";
import TunggakanScanIndicator from "@/components/TunggakanScanIndicator";
import TunggakanRefreshIndicator from "@/components/TunggakanRefreshIndicator";
import { ThemeProvider } from "@/context/ThemeContext";

// Pages
import LandingPage from "@/pages/LandingPage";
import CustomerBills from "@/pages/CustomerBills";
import DigiFlazzBills from "@/pages/DigiFlazzBills";
import TokenListrik from "@/pages/TokenListrik";
import TagihanListrik from "@/pages/TagihanListrik";
import PDAMTebo from "@/pages/PDAMTebo";
import PDAMDigiflazz from "@/pages/PDAMDigiflazz";
import Pulsa from "@/pages/Pulsa";
import CustomerPayment from "@/pages/CustomerPayment";
import CustomerHistory from "@/pages/CustomerHistory";
import PaymentStatus from "@/pages/PaymentStatus";
import InstallmentPayment from "@/pages/InstallmentPayment";
import Receipt from "@/pages/Receipt";
import UserLogin from "@/pages/UserLogin";
import UserRegister from "@/pages/UserRegister";
import UserReports from "@/pages/UserReports";
import TopUpRequest from "@/pages/TopUpRequest";
import PengawasDashboard from "@/pages/PengawasDashboard";
import OfflineMode from "@/pages/OfflineMode";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminPending from "@/pages/admin/AdminPending";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminUpload from "@/pages/admin/AdminUpload";
import AdminReports from "@/pages/admin/AdminReports";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminInstallment from "@/pages/admin/AdminInstallment";
import AdminTransactions from "@/pages/admin/AdminTransactions";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminTopUpRequests from "@/pages/admin/AdminTopUpRequests";
import AdminNotifications from "@/pages/admin/AdminNotifications";
import AdminPengawas from "@/pages/admin/AdminPengawas";
import AdminDatabaseStats from "@/pages/admin/AdminDatabaseStats";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminCustomerLocations from "@/pages/admin/AdminCustomerLocations";
import AdminMasterData from "@/pages/admin/AdminMasterData";
import AdminPetaCode from "@/pages/admin/AdminPetaCode";
import AdminBackup from "@/pages/admin/AdminBackup";
import AdminRekonsiliasi from "@/pages/admin/AdminRekonsiliasi";
import PetaGlobal from "@/pages/PetaGlobal";
import AntarSurat from "@/pages/AntarSurat";
import Tunggakan from "@/pages/Tunggakan";
import PetugasRekonsiliasi from "@/pages/PetugasRekonsiliasi";

// Customer (Pelanggan) Pages
import AuthCallback from "@/pages/AuthCallback";
import CustomerDashboard from "@/pages/CustomerDashboard";
import AdminGmailBot from "@/pages/admin/AdminGmailBot";
import TransferMasuk from "@/pages/TransferMasuk";

import { PaymentGate } from "@/components/PaymentGate";

// Entry gate for "/": unauthenticated visitors see the login screen directly;
// authenticated users are routed straight to their own dashboard.
function Home() {
  const customerToken = localStorage.getItem("customer_token");
  const adminToken = localStorage.getItem("admin_token");
  const userToken = localStorage.getItem("user_token");
  const userRole = (localStorage.getItem("user_role") || "").toLowerCase();

  if (customerToken) return <Navigate to="/pelanggan" replace />;
  if (adminToken) return <Navigate to="/admin/dashboard" replace />;
  if (userToken) {
    if (userRole === "pengawas") return <Navigate to="/pengawas" replace />;
    // Petugas / agen: LandingPage is their working home (service cards).
    return <LandingPage />;
  }
  // Not logged in → show the login screen as the first page.
  return <UserLogin />;
}

// Router wrapper to detect session_id in hash (for OAuth callback)
function AppRouter() {
  const location = useLocation();
  
  // Check URL hash for session_id - handle OAuth callback synchronously during render
  // This prevents race conditions with protected routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Customer Routes */}
      <Route path="/" element={<Home />} />
      <Route path="/beranda" element={<LandingPage />} />
      <Route path="/bills" element={<CustomerBills />} />
      <Route path="/bills-realtime" element={<DigiFlazzBills />} />
      <Route path="/listrik/token" element={<TokenListrik />} />
      <Route path="/listrik/tagihan" element={<TagihanListrik />} />
      <Route path="/pdam-tebo" element={<PDAMTebo />} />
      <Route path="/pdam-digiflazz" element={<PDAMDigiflazz />} />
      <Route path="/pulsa" element={<Pulsa />} />
      <Route path="/payment/:billId" element={<PaymentGate><CustomerPayment /></PaymentGate>} />
      <Route path="/payment" element={<PaymentGate><CustomerPayment /></PaymentGate>} />
      <Route path="/payment-status/:paymentId" element={<PaymentStatus />} />
      <Route path="/history" element={<CustomerHistory />} />
      <Route path="/installment" element={<PaymentGate><InstallmentPayment /></PaymentGate>} />
      <Route path="/receipt" element={<Receipt />} />
      
      {/* User (Petugas/Agen) Routes */}
      <Route path="/login" element={<UserLogin />} />
      <Route path="/register" element={<UserRegister />} />
      <Route path="/my-reports" element={<UserReports />} />
      <Route path="/transfer-masuk" element={<TransferMasuk />} />
      <Route path="/topup" element={<TopUpRequest />} />
      <Route path="/offline-mode" element={<OfflineMode />} />
      <Route path="/peta-global" element={<PetaGlobal />} />
      <Route path="/antar-surat" element={<AntarSurat />} />
      <Route path="/antar-surat/:id" element={<AntarSurat />} />
      <Route path="/tunggakan" element={<Tunggakan />} />
      <Route path="/tunggakan/:id" element={<Tunggakan />} />
      <Route path="/rekonsiliasi" element={<PetugasRekonsiliasi />} />
      
      {/* Pengawas Routes */}
      <Route path="/pengawas" element={<PengawasDashboard />} />
      
      {/* Pelanggan (Google OAuth) Routes */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/pelanggan" element={<CustomerDashboard />} />
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route path="/admin/pending" element={<AdminPending />} />
      <Route path="/admin/customers" element={<AdminCustomers />} />
      <Route path="/admin/upload" element={<AdminUpload />} />
      <Route path="/admin/reports" element={<AdminReports />} />
      <Route path="/admin/settings" element={<AdminSettings />} />
      <Route path="/admin/installment" element={<AdminInstallment />} />
      <Route path="/admin/transactions" element={<AdminTransactions />} />
      <Route path="/admin/users" element={<AdminUsers />} />
      <Route path="/admin/topup-requests" element={<AdminTopUpRequests />} />
      <Route path="/admin/notifications" element={<AdminNotifications />} />
      <Route path="/admin/pengawas" element={<AdminPengawas />} />
      <Route path="/admin/database" element={<AdminDatabaseStats />} />
      <Route path="/admin/analytics" element={<AdminAnalytics />} />
      <Route path="/admin/customer-locations" element={<AdminCustomerLocations />} />
      <Route path="/admin/master-data" element={<AdminMasterData />} />
      <Route path="/admin/peta-code" element={<AdminPetaCode />} />
      <Route path="/admin/backup" element={<AdminBackup />} />
      <Route path="/admin/gmail" element={<AdminGmailBot />} />
      <Route path="/admin/rekonsiliasi" element={<AdminRekonsiliasi />} />
    </Routes>
  );
}

// Flag to ensure we only run the dead-token recovery once (prevents redirect loops).
let handlingDeadToken = false;

// Detail strings that mean the stored JWT is no longer usable (bad signature /
// malformed / user no longer exists). These are NOT the single-device session
// mismatch (which is intentionally disabled); they mean re-login is the only cure.
const DEAD_TOKEN_SIGNALS = [
  "invalid token",
  "admin not found",
  "user tidak ditemukan atau tidak aktif",
  "could not validate credentials",
  "not authenticated",
];

// Register the global axios interceptor ONCE at module load — not inside a
// useEffect — so it is active before any route component fires its first request
// (child effects run before the parent App effect, which otherwise lets the very
// first 401 slip through on a cold page load).
// NOTE: Auto-logout on single-device *session mismatch* stays DISABLED per user request.
// We only force a re-login when the token itself is dead (signature invalid / user gone),
// otherwise the user gets stuck forever: the map still loads (optional-auth endpoints
// tolerate a bad token) while every strict endpoint shows "Invalid token".
axios.interceptors.response.use(
  (res) => res,
  (error) => {
    try {
      const status = error?.response?.status;
      const rawDetail = error?.response?.data?.detail;
      const detail = (typeof rawDetail === "string" ? rawDetail : "").toLowerCase();
      const hasToken = !!(
        localStorage.getItem("user_token") || localStorage.getItem("admin_token")
      );
      const isDeadToken =
        status === 401 && DEAD_TOKEN_SIGNALS.some((s) => detail.includes(s));
      const path = window.location.pathname || "";
      const onLoginPage = path === "/" || path === "/login" || path === "/admin/login";

      if (isDeadToken && hasToken && !handlingDeadToken && !onLoginPage) {
        handlingDeadToken = true;
        const isAdmin =
          path.startsWith("/admin") || !localStorage.getItem("user_token");
        // Clear the dead session.
        [
          "user_token",
          "user_name",
          "user_role",
          "user_balance",
          "user_push_subscribed",
          "admin_token",
          "admin_name",
        ].forEach((k) => localStorage.removeItem(k));
        toast.error("Sesi Anda telah berakhir. Silakan login ulang.");
        setTimeout(() => {
          window.location.href = isAdmin ? "/admin/login" : "/login";
        }, 1200);
      }
    } catch {
      /* never let the interceptor itself throw */
    }
    return Promise.reject(error);
  }
);

function App() {
  return (
    <ThemeProvider>
      <div className="min-h-screen bg-background text-foreground transition-colors duration-300">
        <BrowserRouter>
          <DynamicManifest />
          <AppRouter />
          <InstallPWA />
          <GlobalAutoSync />
          <TunggakanScanIndicator />
          <TunggakanRefreshIndicator />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        <UpdateNotification />
      </div>
    </ThemeProvider>
  );
}

export default App;
